import { useEffect, useState } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Button } from "components/ui/button";
import { DisplayYears, Order, Sort } from "lib/types";
import { Setting, VatInvoice } from "lib/models";
import { apiGet } from "components/Common/Axios";
import toast from "react-hot-toast";
import { RootState, useAppDispatch, useAppSelector } from "store";
import { setSearchRepositStatus, setTaxYears } from "store/mainSlice";
import { cn, formatKoreanDate, getMonth, getYear } from "lib/utils";
import VatPaperPopup from "./VatPaperPopup";
import { Switch } from "components/ui/switch";
import { Label } from "components/ui/label";
import BankLogPopup from "./BankLogPopup";
import BankLogPopupAdd from "./BankLogPopupAdd";
import BankLogPopupSelect from "./BankLogPopupSelect";
import { Checkbox } from "components/ui/checkbox";
import BankLogPopupSelectMulti from "./BankLogPopupSelectMulti";

export type VatInvoiceCheck = {
  vatInvoice: VatInvoice;
  checked: boolean;
};

// 입금확인
const VatReposit = () => {
  const [sort, setSort] = useState<Sort>("region");
  const [order, setOrder] = useState<Order>("asc");
  const [searchString, setSearchString] = useState<string>("");
  const [selectedVatInvoices, setSelectedVatInvoices] = useState<VatInvoice[]>(
    []
  );
  const [selectedVatInvoiceMulti, setSelectedVatInvoiceMulti] = useState<
    VatInvoice[]
  >([]);

  const [selectedVatInvoice, setSelectedVatInvoice] = useState<VatInvoice>();
  const [isShowComplete, setIsShowComplete] = useState<boolean>(false);

  const [isBankLogOpen, setIsBankLogOpen] = useState<boolean>(false);
  const [isBankLogOpenAdd, setIsBankLogOpenAdd] = useState<boolean>(false);
  const [isBankLogOpenSelect, setIsBankLogOpenSelect] =
    useState<boolean>(false);
  // 여러개의 계산서 입금처리하기
  const [isBankLogOpenSelectMulti, setIsBankLogOpenSelectMulti] =
    useState<boolean>(false);

  const [isVatPaperOpen, setIsVatPaperOpen] = useState<boolean>(false);
  const [selectMonth, setSelectMonth] = useState<number>(0);

  // [VatInvoiceCheck List]
  // 모든 VatInvoiceCheck
  const [totalVatInvoiceCheck, setTotalVatInvoiceCheck] = useState<
    VatInvoiceCheck[]
  >([]);

  // 검색조건에 Filtering된 (화면에 보이는) VatInvoiceCheck
  const [filteredInvoiceCheck, setFilteredInvoiceCheck] = useState<
    VatInvoiceCheck[]
  >([]);

  const [setting, setSetting] = useState<Setting | undefined>(undefined);

  const me = useAppSelector((state: RootState) => state.user.me);

  const searchOption = useAppSelector(
    (state) => state.main.searchRepositOption
  );
  const taxYears = useAppSelector((state: RootState) => state.main.taxYears);
  const dispatch = useAppDispatch();

  const loadVatInvoices = async () => {
    const response = await apiGet("/reposit");
    if (response.error) {
      toast.error("세금계산서를 불러오는데 실패했습니다.");
      console.error("loadVatInvoices.error", response.error);
      return;
    }

    const vatInvoices = response.data as VatInvoice[];
    setTotalVatInvoiceCheck(
      vatInvoices.map((vatInvoice) => {
        return { vatInvoice: { ...vatInvoice }, checked: false };
      })
    );
  };

  const loadSetting = async () => {
    try {
      const response = await apiGet("/setting");
      if (response.error) {
        toast.error("시스템 설정을 불러오는데 실패했습니다.");
        console.error("loadSetting.error", response.error);
        return;
      }
      setSetting(response.data);
    } catch (error: any) {
      toast.error("시스템 설정을 불러오는데 실패했습니다.");
      console.error("loadSetting.error", error.message);
    }
  };

  useEffect(() => {
    if (searchOption) {
      setSearchString(searchOption.keyword);
      setSort(searchOption.sort as Sort);
      setOrder(searchOption.order as Order);
      setIsShowComplete(searchOption.isShowComplete);
      setSelectMonth(searchOption.selectMonth);
    }
    loadVatInvoices();
    loadSetting();
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // searchOption들이 바뀌면 다시 적용
  useEffect(() => {
    // store에 현재상태 저장
    dispatch(
      setSearchRepositStatus({
        keyword: searchString,
        sort,
        order,
        status: [],
        isShowComplete,
        selectMonth,
        isShowFree: false,
      })
    );

    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    totalVatInvoiceCheck,
    searchString,
    sort,
    order,
    isShowComplete,
    taxYears,
    selectMonth,
    dispatch,
  ]);

  const getCheckedVatInvoice = () => {
    return filteredInvoiceCheck.filter((s) => s.checked);
  };

  useEffect(() => {
    setSelectedVatInvoiceMulti(getCheckedVatInvoice().map((s) => s.vatInvoice));
  }, [filteredInvoiceCheck]);

  // 검색시 현재 검색어에 맞게 필터링
  const search = () => {
    const filteredInvoice = totalVatInvoiceCheck.filter((s) => {
      return (
        (s.vatInvoice.vatCompany?.nickName.includes(searchString) ||
          s.vatInvoice.vatCompany?.name.includes(searchString) ||
          s.vatInvoice.vatCompany?.ceoName.includes(searchString) ||
          s.vatInvoice.vatCompany?.address.includes(searchString) ||
          s.vatInvoice.vatCompany?.depositName.includes(searchString) ||
          s.vatInvoice.schoolSales?.filter((s) =>
            s.school.name.includes(searchString)
          ).length > 0 ||
          s.vatInvoice.schoolSalesMaterial?.filter((s) =>
            s.school.name.includes(searchString)
          ).length > 0) &&
        (isShowComplete || !s.vatInvoice.isDepositCompleted)
      );
    });

    // 필터에서 걸러지면 체크해제
    // setTotalVatInvoiceCheck(
    //   totalVatInvoiceCheck.map((tic: VatInvoiceCheck) => {
    //     if (
    //       !filteredInvoice.some(
    //         (fic) => fic.vatInvoice.id === tic.vatInvoice.id
    //       )
    //     ) {
    //       return { ...tic, checked: false };
    //     }
    //     return tic;
    //   })
    // );

    if (sort === "contract_date") {
      // 발급일
      if (order === "asc") {
        filteredInvoice.sort((a, b) => {
          return a.vatInvoice.issuedDate && b.vatInvoice.issuedDate
            ? a.vatInvoice.issuedDate > b.vatInvoice.issuedDate
              ? 1
              : -1
            : a.vatInvoice.issuedDate && !b.vatInvoice.issuedDate
            ? 1
            : !a.vatInvoice.issuedDate && b.vatInvoice.issuedDate
            ? -1
            : 1;
        });
      } else {
        filteredInvoice.sort((a, b) => {
          return a.vatInvoice.issuedDate && b.vatInvoice.issuedDate
            ? a.vatInvoice.issuedDate > b.vatInvoice.issuedDate
              ? -1
              : 1
            : a.vatInvoice.issuedDate && !b.vatInvoice.issuedDate
            ? -1
            : !a.vatInvoice.issuedDate && b.vatInvoice.issuedDate
            ? 1
            : -1;
        });
      }
    } else if (sort === "class_date") {
      // 입금일
      if (order === "asc") {
        filteredInvoice.sort((a, b) => {
          return a.vatInvoice.depositDate && b.vatInvoice.depositDate
            ? a.vatInvoice.depositDate > b.vatInvoice.depositDate
              ? 1
              : -1
            : a.vatInvoice.depositDate && !b.vatInvoice.depositDate
            ? 1
            : !a.vatInvoice.depositDate && b.vatInvoice.depositDate
            ? -1
            : 1;
        });
      } else {
        filteredInvoice.sort((a, b) => {
          return a.vatInvoice.depositDate && b.vatInvoice.depositDate
            ? a.vatInvoice.depositDate > b.vatInvoice.depositDate
              ? -1
              : 1
            : a.vatInvoice.depositDate && !b.vatInvoice.depositDate
            ? -1
            : !a.vatInvoice.depositDate && b.vatInvoice.depositDate
            ? 1
            : -1;
        });
      }
    } else if (sort === "name") {
      if (order === "asc") {
        filteredInvoice.sort((a, b) => {
          return (a.vatInvoice.vatCompany?.name ?? "") >
            (b.vatInvoice.vatCompany?.name ?? "")
            ? 1
            : -1;
        });
      } else {
        filteredInvoice.sort((a, b) => {
          return (a.vatInvoice.vatCompany?.name ?? "") <
            (b.vatInvoice.vatCompany?.name ?? "")
            ? 1
            : -1;
        });
      }
    } else if (sort === "region") {
      if (order === "asc") {
        filteredInvoice.sort((a, b) => {
          return (a.vatInvoice.vatCompany?.address ?? "") >
            (b.vatInvoice.vatCompany?.address ?? "")
            ? 1
            : -1;
        });
      } else {
        filteredInvoice.sort((a, b) => {
          return (a.vatInvoice.vatCompany?.address ?? "") <
            (b.vatInvoice.vatCompany?.address ?? "")
            ? 1
            : -1;
        });
      }
    }

    const filteredInv = filteredInvoice.filter(
      (s) =>
        taxYears.includes(getYear(s.vatInvoice.issuedDate!)) &&
        (taxYears.length !== 1 ||
          selectMonth === 0 ||
          getMonth(s.vatInvoice.issuedDate!) === selectMonth)
    );

    setFilteredInvoiceCheck(filteredInv);
  };

  const popupBankLogSelect = (vatInvoice: VatInvoice) => {
    if (!vatInvoice) {
      console.error("vatInvoice = undefined");
      return;
    }
    setSelectedVatInvoice(vatInvoice);
    setIsBankLogOpenSelect(true);
  };

  const popupVatPaper = (vatInvoice: VatInvoice) => {
    if (!vatInvoice) {
      console.error("vatInvoice = undefined");
      return;
    }
    setSelectedVatInvoices([vatInvoice]);
    setIsVatPaperOpen(true);
  };

  const handleYearClick = (year: number) => {
    if (taxYears.includes(year)) {
      dispatch(setTaxYears(taxYears.filter((y) => y !== year)));
    } else {
      dispatch(setTaxYears([...taxYears, year]));
    }
  };

  const handleVatInvoiceCheckChange = (checked: boolean, id: number) => {
    setFilteredInvoiceCheck(
      filteredInvoiceCheck.map((invoiceChecked) => {
        if (invoiceChecked.vatInvoice.id === id) {
          return { ...invoiceChecked, checked };
        } else {
          return invoiceChecked;
        }
      })
    );
  };

  const handlePopupBankLogSelectOrMulti = (vattInvoice: VatInvoice) => {
    if (vattInvoice.bankLogMultiVatInvoice) {
      setSelectedVatInvoiceMulti(
        vattInvoice.bankLogMultiVatInvoice.vatInvoices
      );
      console.error(
        "vattInvoice.bankLogMultiVatInvoice.vatInvoices",
        vattInvoice.bankLogMultiVatInvoice.vatInvoices
      );
      setIsBankLogOpenSelectMulti(true);
    } else {
      setSelectedVatInvoice(vattInvoice);
      setIsBankLogOpenSelect(true);
    }
  };

  if (!setting) {
    return null;
  }

  return (
    <div className="text-sm pb-[100px]">
      <BankLogPopup
        open={isBankLogOpen}
        onOpenChange={setIsBankLogOpen}
        setting={setting}
      />
      <BankLogPopupAdd
        open={isBankLogOpenAdd}
        onOpenChange={setIsBankLogOpenAdd}
      />
      <BankLogPopupSelect
        open={isBankLogOpenSelect}
        onOpenChange={setIsBankLogOpenSelect}
        vatInvoice={selectedVatInvoice}
        reload={loadVatInvoices}
        setting={setting}
      />
      <BankLogPopupSelectMulti
        open={isBankLogOpenSelectMulti}
        onOpenChange={setIsBankLogOpenSelectMulti}
        vatInvoices={selectedVatInvoiceMulti}
        reload={loadVatInvoices}
        setting={setting}
      />

      <VatPaperPopup
        open={isVatPaperOpen}
        onOpenChange={setIsVatPaperOpen}
        vatInvoices={selectedVatInvoices}
        totalVatInvoiceCheck={totalVatInvoiceCheck}
        setTotalVatInvoiceCheck={setTotalVatInvoiceCheck}
        setting={setting}
        isApplyMode={false}
        isReadOnly={true}
      />
      <div className="flex flex-col xl:flex-row xl:justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50/70 px-4 py-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div>정렬</div>
          <Select value={sort} onValueChange={(v) => setSort(v as Sort)}>
            <SelectTrigger className="w-[120px] text-center">
              <SelectValue placeholder="1" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="contract_date">발급일</SelectItem>
                <SelectItem value="class_date">입금일</SelectItem>
                <SelectItem value="name">거래처명</SelectItem>
                <SelectItem value="region">지역</SelectItem>
                <SelectItem value="stage">단계</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          <Select value={order} onValueChange={(v) => setOrder(v as Order)}>
            <SelectTrigger className="w-[120px] text-center">
              <SelectValue placeholder="1" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="asc">오름차순</SelectItem>
                <SelectItem value="desc">내림차순</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>

          <div>검색</div>
          <input
            className="px-4 py-2 border border-gray-200 rounded-md w-[150px]"
            type="text"
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
          />

          <div className="xl:pl-4 flex items-center gap-2">
            <Switch
              checked={isShowComplete}
              onCheckedChange={(checked) => setIsShowComplete(checked)}
              id="isShowComplete"
            />
            <Label htmlFor="isShowComplete">입금완료 포함</Label>
          </div>
          <div className="flex flex-wrap items-center xl:px-4 gap-2">
            {DisplayYears.map((year) => (
              <div
                key={year}
                className={cn(
                  "text-xs rounded-md py-2 px-3 cursor-pointer w-[80px] text-center",
                  taxYears.includes(year)
                    ? "bg-indigo-600 text-white border border-indigo-600 shadow-sm"
                    : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                )}
                onClick={() => handleYearClick(year)}
              >
                {year}년
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {selectedVatInvoiceMulti.length > 1 && (
            <Button
              onClick={() => setIsBankLogOpenSelectMulti(true)}
              variant="primary"
            >
              여러개의 계산서 입금처리하기
            </Button>
          )}
          <Button
            onClick={() => setIsBankLogOpenAdd(true)}
            variant="destructive"
          >
            입출금내역 추가
          </Button>
          <Button onClick={() => setIsBankLogOpen(true)} variant="primary">
            입출금내역 보기
          </Button>
        </div>
      </div>
      {taxYears.length === 1 && (
        <div className="flex flex-wrap justify-center items-center gap-2 py-4">
          <div
            className={cn(
              "w-12 h-8 rounded-sm border-solid border border-gray-300 cursor-pointer flex items-center justify-center",
              selectMonth === 0
                ? "bg-indigo-600 text-white border border-indigo-600 shadow-sm"
                : "text-gray-800 hover:bg-gray-50"
            )}
            onClick={() => setSelectMonth(0)}
          >
            전체
          </div>
          {Array.from({ length: 12 }, (_, i) => (
            <div
              key={i}
              className={cn(
                "w-8 h-8 rounded-sm border-solid border border-gray-300 cursor-pointer flex items-center justify-center",
                selectMonth === i + 1
                  ? "bg-indigo-600 text-white border border-indigo-600 shadow-sm"
                  : "text-gray-800 hover:bg-gray-50"
              )}
              onClick={() => setSelectMonth(i + 1)}
            >
              <div>{i + 1}</div>
            </div>
          ))}
        </div>
      )}
      <div className="overflow-x-auto">
      <div className="min-w-[1500px]">
      <div className="grid grid-cols-[50px_50px_50px_120px_120px_120px_160px_160px_1fr_120px_120px_140px_140px_80px] border-solid border-b border-t border-gray-200 py-2 bg-gray-50 text-gray-500 font-medium pl-4">
        <div></div>
        <div className="text-center">입금</div>
        <div className="text-center">VID</div>
        <div className="">학교명</div>
        <div className="">거래처명</div>
        <div className="">발급처명</div>
        <div className="">입금자명</div>
        <div className="">품목명</div>
        <div className="">담당자</div>
        <div className="text-right">총액</div>
        <div className="text-right">입금액</div>
        <div className="text-center">발급일</div>
        <div className="text-center">입금일</div>
        <div className="text-center">입금확인</div>
      </div>
      <div>
        {filteredInvoiceCheck.map((s: VatInvoiceCheck, index: number) => (
          <div
            key={index}
            //onClick={(e) => popupBankLogSelect(s.vatInvoice)}
            onClick={(e) => handlePopupBankLogSelectOrMulti(s.vatInvoice)}
            className={cn(
              "grid grid-cols-[50px_50px_50px_120px_120px_120px_160px_160px_1fr_120px_120px_140px_140px_80px] border-solid border-b border-gray-200 pl-4 py-2 items-center cursor-pointer",
              s.checked
                ? "bg-indigo-50 hover:bg-indigo-100"
                : "bg-white hover:bg-gray-50"
            )}
          >
            <div>
              <Checkbox
                className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                checked={s.checked}
                onCheckedChange={(checked: boolean) =>
                  handleVatInvoiceCheckChange(checked, s.vatInvoice.id)
                }
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
            </div>
            <div className="flex items-center justify-center gap-x-2">
              {s.vatInvoice.isDepositCompleted && (
                <div className="bg-cyan-500 rounded-md w-12 h-[25px] text-center text-white text-xs pt-[4px]">
                  입금
                </div>
              )}
            </div>
            <div className="text-center">{s.vatInvoice.id}</div>
            <div
              className="whitespace-pre-wrap text-indigo-600"
              onClick={(e) => {
                e.stopPropagation();
                popupVatPaper(s.vatInvoice);
              }}
            >
              {Array.from(
                new Set(
                  s.vatInvoice.schoolSalesMaterial
                    .map((sales) => sales.school?.name ?? "")
                    .concat(
                      s.vatInvoice.schoolSales.map(
                        (sales) => sales.school?.name ?? ""
                      )
                    )
                )
              ).join("\n")}
            </div>
            <div>{s.vatInvoice.vatCompany?.nickName}</div>

            <div>{s.vatInvoice.vatCompany?.name}</div>
            <div>{s.vatInvoice.vatCompany?.depositName}</div>
            <div>{s.vatInvoice.supplyContent1}</div>
            <div>
              {s.vatInvoice.schoolSales.length > 0
                ? s.vatInvoice.schoolSales[0].user?.name
                : s.vatInvoice.schoolSalesMaterial.length > 0
                ? s.vatInvoice.schoolSalesMaterial[0].user?.name
                : ""}
            </div>
            <div className="text-right">
              {s.vatInvoice.totalAmt.toLocaleString()}
            </div>
            <div className="text-right">
              {s.vatInvoice.depositAmt.toLocaleString()}
            </div>
            <div className="text-center">
              {formatKoreanDate(s.vatInvoice.issuedDate!)}
            </div>
            <div className="text-center">
              {s.vatInvoice.depositDate
                ? formatKoreanDate(s.vatInvoice.depositDate!)
                : ""}
            </div>
            <div className="flex flex-col gap-2">
              {s.vatInvoice.bankLogMultiVatInvoice && (
                <div className="text-right px-2 py-1 bg-indigo-100 text-indigo-800 rounded-md">
                  {s.vatInvoice.depositAmt.toLocaleString()}
                </div>
              )}

              {s.vatInvoice.bankLogs &&
                s.vatInvoice.bankLogs.length > 0 &&
                s.vatInvoice.bankLogs?.map((log, index2) => (
                  <div
                    key={index * 1000 + index2}
                    className={`text-right ${
                      s.vatInvoice.bankLogs && s.vatInvoice.bankLogs.length > 0
                        ? "px-2 py-1 bg-indigo-100 text-indigo-800 rounded-md"
                        : ""
                    }`}
                  >
                    {log.depositAmt.toLocaleString()}
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
      </div>
      </div>
      <div>
        <div className="flex flex-wrap items-center gap-x-10 gap-y-2 pl-2 sm:pl-5 py-3">
          <div className="flex items-center gap-2">
            <div className="text-gray-500">입금할 총액</div>
            <div>
              {filteredInvoiceCheck
                .reduce((acc, cur) => acc + cur.vatInvoice.totalAmt, 0)
                .toLocaleString()}
              원
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-gray-500">입금받은 총액</div>
            <div>
              {filteredInvoiceCheck
                .reduce((acc, cur) => acc + cur.vatInvoice.depositAmt, 0)
                .toLocaleString()}
              원
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-gray-500">미입금액</div>
            <div>
              {filteredInvoiceCheck
                .reduce(
                  (acc, cur) =>
                    acc + (cur.vatInvoice.totalAmt - cur.vatInvoice.depositAmt),
                  0
                )
                .toLocaleString()}
              원
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VatReposit;
